/**
 * SynapSee 2026 Theme
 * Modern theme with vibrant red, warm orange, and neutral accents
 * Primary: Red (#ED1C24), Secondary: Orange (#E97E27), Tertiary: Light Gray (#F0F0F0)
 */

import { lucianoTardinColors, baseColors } from '../tokens/colors.js';
import { spacing, zIndex } from '../tokens/spacing.js';
import { typography } from '../tokens/typography.js';
import { borderRadius, shadows, transitions } from '../tokens/effects.js';

export const synapsee2026Theme = {
  name: 'synapsee-2026',
  label: 'SynapSee 2026',
  description: 'Modern theme with vibrant red, warm orange, and neutral accents',
  isDark: false,

  // Color palette
  colors: {
    primary: lucianoTardinColors.primary,
    primaryLight: lucianoTardinColors.primaryLight,
    primaryDark: lucianoTardinColors.primaryDark,
    primaryHover: lucianoTardinColors.primaryHover,
    primaryOpacity: lucianoTardinColors.primaryOpacity20,

    secondary: lucianoTardinColors.secondary,
    secondaryLight: lucianoTardinColors.secondaryLight,
    secondaryDark: lucianoTardinColors.secondaryDark,
    secondaryOpacity: lucianoTardinColors.secondaryOpacity20,

    tertiary: lucianoTardinColors.tertiary,
    tertiaryLight: lucianoTardinColors.tertiaryLight,
    tertiaryDark: lucianoTardinColors.tertiaryDark,
    tertiaryOpacity: lucianoTardinColors.tertiaryOpacity20,

    accent: lucianoTardinColors.accent,
    accentLight: lucianoTardinColors.accentLight,
    accentDark: lucianoTardinColors.accentDark,
    accentOpacity: lucianoTardinColors.accentOpacity20,

    // Semantic colors
    error: lucianoTardinColors.error,
    errorLight: lucianoTardinColors.errorLight,
    success: lucianoTardinColors.success,
    successLight: lucianoTardinColors.successLight,
    warning: lucianoTardinColors.warning,
    warningLight: lucianoTardinColors.warningLight,
    info: lucianoTardinColors.info,
    infoLight: lucianoTardinColors.infoLight,

    // Neutrals
    background: lucianoTardinColors.backgroundLight,
    backgroundAlt: lucianoTardinColors.backgroundDark,
    backgroundDark: lucianoTardinColors.backgroundDark,
    backgroundNeutral: lucianoTardinColors.backgroundNeutral,
    surface: lucianoTardinColors.surfaceLight,
    surfaceElevated: lucianoTardinColors.surfaceElevated,

    // Text
    textPrimary: lucianoTardinColors.darkGray900,
    textSecondary: lucianoTardinColors.mediumGray600,
    textTertiary: lucianoTardinColors.mediumGray500,
    textInverse: baseColors.white,

    // Borders
    borderDefault: lucianoTardinColors.lightGray300,
    borderSubtle: lucianoTardinColors.lightGray200,
    borderInput: lucianoTardinColors.borderInput,
  },

  // Spacing
  spacing,

  // Z-index
  zIndex,

  // Typography
  typography,

  // Border radius
  borderRadius,

  // Shadows
  shadows,

  // Transitions
  transitions,

  // Component-specific tokens
  components: {
    button: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      transition: transitions.default,
      fontSize: typography.fontSize.body,
      fontWeight: typography.fontWeight.semibold,
      // SYN-2738 (A9): fundos/texto do DSButton por tema (--button-*).
      primaryBg: lucianoTardinColors.primaryHover,
      primaryBgHover: lucianoTardinColors.primaryDark,
      // C12 (2026-10-05): texto vermelho do botão ghost/de texto em {colors.acao}
      // (#D41920, 5,30:1 sobre branco). O vermelho da marca puro (#ED1C24) dá
      // 4,38:1 — abaixo de AA para texto de 14-16px ("Voltar", "Já conheço").
      ghostText: lucianoTardinColors.primaryHover,
      secondaryBg: lucianoTardinColors.secondaryButton,
      secondaryBgHover: lucianoTardinColors.secondaryButtonHover,
      accentText: lucianoTardinColors.white,
    },
    card: {
      padding: spacing.xl,
      borderRadius: borderRadius.lg,
      backgroundColor: lucianoTardinColors.surfaceLight,
      boxShadow: shadows.base,
    },
    input: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      borderColor: lucianoTardinColors.lightGray200,
      focusBorderColor: lucianoTardinColors.primary,
      fontSize: typography.fontSize.body,
    },
    chip: {
      paddingX: spacing.md,
      paddingY: spacing.xs,
      borderRadius: borderRadius.full,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.medium,
    },
    badge: {
      paddingX: spacing.sm,
      paddingY: spacing.xs,
      borderRadius: borderRadius.sm,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.bold,
    },
    sidebar: {
      backgroundColor: lucianoTardinColors.backgroundLight,
      iconSelected: lucianoTardinColors.primary,
      iconDefault: lucianoTardinColors.mediumGray600,
      iconDisabled: lucianoTardinColors.lightGray200,
      iconStatic: lucianoTardinColors.darkGray900,
      divider: lucianoTardinColors.lightGray200,
    },
  },

  // Typography styles (hero, featured, lead text)
  typographyStyles: {
    heroTitle: typography.styles.heroTitle,
    heroSubtitle: typography.styles.heroSubtitle,
    featuredText: typography.styles.featuredText,
    leadText: typography.styles.leadText,
  },

  // Vuetify theme configuration
  vuetifyTheme: {
    dark: false,
    colors: {
      primary: lucianoTardinColors.primary,
      secondary: lucianoTardinColors.secondary,
      tertiary: lucianoTardinColors.tertiary,
      accent: lucianoTardinColors.accent,
      error: lucianoTardinColors.error,
      warning: lucianoTardinColors.warning,
      info: lucianoTardinColors.info,
      success: lucianoTardinColors.success,
      background: lucianoTardinColors.backgroundLight,
      surface: lucianoTardinColors.surfaceLight,
    },
  },
};
